// frontend/src/pages/admin/ExternalTokens/OverviewTab.js
import React, { useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, Clock, KeyRound, RefreshCw,
  Server, ShieldCheck, XCircle, ArrowRight,
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { useApiOperationsOverview } from '../../../hooks/useApiOperationsData';
import StatusBadge from '../../../components/UI/StatusBadge';
import PillToggle from '../../../components/UI/PillToggle';
import { formatDate, formatDateShort, formatDateTime } from '../../../utils/dateUtils';

const DAY_OPTIONS = [
  { id: '7', label: '7d' },
  { id: '14', label: '14d' },
  { id: '30', label: '30d' },
];

const MetricCard = ({ icon: Icon, iconBg, iconColor, value, label, sub, subClassName = 'text-amber-600' }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
    <div className="flex items-center">
      <div className={`w-9 h-9 sm:w-12 sm:h-12 ${iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor}`} />
      </div>
      <div className="ml-2 sm:ml-4 min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
        {sub && <p className={`text-xs mt-0.5 truncate ${subClassName}`}>{sub}</p>}
      </div>
    </div>
  </div>
);

/** Plain HTML legend — matches TalentInsights chart pattern (not Recharts Legend). */
const ChartSeriesLegend = () => (
  <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600" aria-label="Chart series">
    <li className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full bg-blue-600 flex-shrink-0" aria-hidden="true" />
      Requests
    </li>
    <li className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full bg-red-600 flex-shrink-0" aria-hidden="true" />
      Errors
    </li>
  </ul>
);

const OverviewSkeleton = () => (
  <div className="animate-pulse space-y-6">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full" />
            <div className="ml-2 sm:ml-4 flex-1">
              <div className="h-6 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="h-64 bg-gray-200 rounded-2xl" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="h-48 bg-gray-200 rounded-2xl" />
      <div className="h-48 bg-gray-200 rounded-2xl" />
    </div>
  </div>
);

const OverviewTab = ({ onNavigateTab }) => {
  const [days, setDays] = useState('7');
  const { data, isLoading, isFetching, isError, refetch } = useApiOperationsOverview(Number(days));

  const summary = data?.summary || {};
  const topIntegrations = data?.top_integrations || [];
  const failing = data?.top_failing_endpoints || [];
  const attention = data?.attention || { expiring_keys: [], high_error_integrations: [] };

  // Axis: compact dd MMM (DS formatDateShort). Tooltip: full dd MMM yyyy (formatDate).
  // Never slice ISO strings — "08-08" is machine format, not HR-operator language.
  const chartData = useMemo(
    () => (data?.timeseries || []).map((row) => ({
      ...row,
      label: row.date ? formatDateShort(row.date) : '',
      dateLabel: row.date ? formatDate(row.date) : '',
    })),
    [data?.timeseries],
  );

  if (isLoading) return <OverviewSkeleton />;

  if (isError) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Couldn&apos;t load overview</h3>
        <p className="text-sm text-gray-500 mb-4">Check your connection and try again.</p>
        <button type="button" onClick={() => refetch()} className="btn btn-admin-primary">
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  // Traffic cards follow the selected window (same horizon as the chart).
  // Active Keys stays inventory-wide — not a time-series metric.
  const requestsWindow = summary.requests_window ?? 0;
  const errorsWindow = summary.errors_window ?? 0;
  const errorRateWindow = requestsWindow
    ? Math.round((errorsWindow / requestsWindow) * 1000) / 10
    : 0;
  const avgLatencyWindow = summary.avg_response_ms_window;
  const windowLabel = `${days}d`;
  const highErrorIntegrations = attention.high_error_integrations || [];
  const expiringKeys = attention.expiring_keys || [];

  const hasAttention =
    expiringKeys.length > 0
    || highErrorIntegrations.length > 0
    || errorsWindow > 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">API health at a glance</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Request logs retained for {data?.retention_days || 90} days. Metadata only — no payloads or secrets.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <PillToggle
            theme="admin"
            variant="compact"
            value={days}
            onChange={setDays}
            options={DAY_OPTIONS}
            ariaLabel="Overview window"
            layout="sectionHeader"
          />
          <button
            type="button"
            onClick={() => refetch()}
            className="icon-button-admin"
            title="Refresh"
            aria-label="Refresh overview"
            disabled={isFetching}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <MetricCard
          icon={ShieldCheck}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          value={summary.active_keys ?? 0}
          label="Active Keys"
          sub={summary.expiring_keys > 0 ? `${summary.expiring_keys} expiring soon` : null}
        />
        <MetricCard
          icon={Activity}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          value={requestsWindow.toLocaleString()}
          label={`Requests (${windowLabel})`}
          sub={(summary.requests_24h || 0) > 0 ? `${(summary.requests_24h || 0).toLocaleString()} in last 24h` : null}
          subClassName="text-gray-500"
        />
        <MetricCard
          icon={XCircle}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          value={errorsWindow.toLocaleString()}
          label={`Errors (${windowLabel})`}
          sub={requestsWindow > 0 ? `${errorRateWindow}% error rate` : null}
          subClassName={errorsWindow > 0 ? 'text-red-600' : 'text-gray-500'}
        />
        <MetricCard
          icon={Clock}
          iconBg="bg-orange-100"
          iconColor="text-orange-600"
          value={avgLatencyWindow != null ? `${avgLatencyWindow}ms` : '—'}
          label={`Avg latency (${windowLabel})`}
        />
      </div>

      {hasAttention && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">Needs attention</p>
                <p className="text-sm text-amber-800 mt-0.5">
                  Review expiring credentials and elevated error rates before they disrupt integrations.
                </p>
              </div>
              {expiringKeys.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-800/80 mb-1.5">
                    Expiring keys
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {expiringKeys.map((key) => (
                      <button
                        key={key.id}
                        type="button"
                        onClick={() => onNavigateTab?.('keys', { search: key.app_name })}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-amber-200 text-xs font-medium text-amber-900 hover:border-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        {key.app_name}
                        <StatusBadge status="expiring_soon" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {highErrorIntegrations.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-800/80 mb-1.5">
                    High error rate (≥5% in {windowLabel})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {highErrorIntegrations.map((item) => (
                      <button
                        key={item.key_id}
                        type="button"
                        onClick={() => onNavigateTab?.('logs', {
                          status_class: 'error',
                          key_id: item.key_id,
                        })}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-amber-200 text-xs font-medium text-amber-900 hover:border-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      >
                        <Server className="w-3.5 h-3.5" />
                        <span className="truncate max-w-[10rem]">{item.app_name}</span>
                        <span className="text-red-600 font-semibold tabular-nums">{item.error_rate}%</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {errorsWindow > 0 && (
                <button
                  type="button"
                  onClick={() => onNavigateTab?.('logs', { status_class: 'error' })}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
                >
                  View {errorsWindow.toLocaleString()} error{errorsWindow === 1 ? '' : 's'} in Request Logs
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[360px] flex flex-col overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Request volume</h3>
            <p className="text-xs text-gray-500">Last {days} days — requests vs errors</p>
          </div>
          {chartData.length > 0 && <ChartSeriesLegend />}
        </div>
        {chartData.length === 0 ? (
          <div className="text-center py-12 flex-1 flex flex-col justify-center">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No traffic yet</h3>
            <p className="text-sm text-gray-500">External API calls will appear here once integrations start calling the portal.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={36} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.dateLabel || ''}
                />
                <Area type="monotone" dataKey="requests" name="Requests" stroke="#2563eb" fill="#dbeafe" strokeWidth={2} />
                <Area type="monotone" dataKey="errors" name="Errors" stroke="#dc2626" fill="#fee2e2" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Top integrations</h3>
            <button
              type="button"
              onClick={() => onNavigateTab?.('keys')}
              className="text-xs font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
            >
              Manage keys
            </button>
          </div>
          {topIntegrations.length === 0 ? (
            <div className="text-center py-12">
              <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No integration traffic</h3>
              <p className="text-sm text-gray-500">Usage will rank here by request volume.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {topIntegrations.map((item) => (
                <li key={item.key_id} className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.app_name}</p>
                    <p className="text-xs text-gray-500 font-mono">{item.key_prefix}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900">{item.requests.toLocaleString()}</p>
                    <p className={`text-xs ${item.errors > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {item.errors} err · {item.error_rate}%
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Top failing endpoints</h3>
            <button
              type="button"
              onClick={() => onNavigateTab?.('logs', { status_class: 'error' })}
              className="text-xs font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
            >
              Open logs
            </button>
          </div>
          {failing.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No recent failures</h3>
              <p className="text-sm text-gray-500">HTTP 4xx/5xx responses in this window will list here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {failing.map((item) => (
                <li key={`${item.method}-${item.endpoint}`} className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{item.method}</p>
                    <code className="text-xs text-gray-800 font-mono break-all">{item.endpoint}</code>
                  </div>
                  <span className="text-sm font-semibold text-red-600 flex-shrink-0">{item.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {data?.generated_at && (
        <p className="text-xs text-gray-400 text-right">
          Generated {formatDateTime(data.generated_at)}
        </p>
      )}
    </div>
  );
};

export default OverviewTab;
